import { FC, useState, useMemo } from "react";
import { Data, UserData } from "../../../types/org.type";
import { useStyles } from "./Documents.styles";
import { Text, Image, Divider } from "@mantine/core";
import { toast } from "react-toastify";
import downloadbutton from "../../../assets/download_24px.svg";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import useGetDocument from "@funded-here-interface/common/src/hooks/useGetDocument";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import {
  FileType,
  SpvFile,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import useAdminUploadDocument from "../../../hooks/useAdminUploadDocument";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import {
  AdminUploadDocumentReponse,
  AdminUploadDocumentRequest,
} from "../../../services/adminUploadDocument";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import DocumentList from "./Components/DocumentList";
import { spvFiles } from "@funded-here-interface/common/src/constant/constant";

interface DocumentsProps {
  data: Data | undefined;
  userData: UserData | undefined;
  role?: Persona;
}

interface DocumentConfig {
  field: keyof DocumentsUpload;
  header: string | ((role?: Persona) => string);
  fileType: FileEnum;
}

const Documents: FC<DocumentsProps> = ({ data, userData, role }) => {
  const { classes } = useStyles();
  const token = useSelector((state: RootState) => state.auth.token);
  const useGetDocumentMutaiton = useGetDocument();
  const useAdminUploadDocumentMutation = useAdminUploadDocument();

  const [documentPaths, setDocumentPaths] = useState({
    businessProfile: data?.documentsUpload?.businessProfile || [],
    shareHoldersDirectorsRegisters:
      data?.documentsUpload?.shareHoldersDirectorsRegisters || [],
    balanceSheetPAndLStatement:
      data?.documentsUpload?.balanceSheetPAndLStatement || [],
    proofOfAddress: data?.documentsUpload?.proofOfAddress || [],
    financialStatement: data?.documentsUpload?.financialStatement || [],
    salaryProof: data?.documentsUpload?.salaryProof || [],
    investmentMandate: data?.documentsUpload?.investmentMandate || [],
    fundManagerLicense: data?.documentsUpload?.fundManagerLicense || [],
    bankStatement: data?.documentsUpload?.bankStatement || [],
    certificateOfIncorporation:
      data?.documentsUpload?.certificateOfIncorporation || [],
    supportingDocumentsWithSuppliers:
      data?.documentsUpload?.supportingDocumentsWithSuppliers || [],
    proofOfBankAccountDetails:
      data?.documentsUpload?.proofOfBankAccountDetails || [],
    crsSelCertificationForm:
      data?.documentsUpload?.crsSelCertificationForm || [],
    companyConstitution: data?.documentsUpload?.companyConstitution || [],
  });

  const additionalDocs = data?.documentsUpload?.additionals || [];
  const hasSpvCompany = data?.spv;
  let spvFilesList = [];
  let additionalFilesList = [];

  if (hasSpvCompany && additionalDocs.length > 0) {
    for (const doc of additionalDocs) {
      if (spvFiles.includes(doc.docName as SpvFile)) {
        spvFilesList.push(doc);
      } else {
        additionalFilesList.push(doc);
      }
    }
  } else {
    additionalFilesList = [...additionalDocs];
  }

  const documentMapping: Record<FileEnum, keyof DocumentsUpload> = {
    [FileEnum.BUSINESS_PROFILE]: "businessProfile",
    [FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS]:
      "shareHoldersDirectorsRegisters",
    [FileEnum.BALANCE_SHEET]: "balanceSheetPAndLStatement",
    [FileEnum.PROOF_OF_ADDRESS]: "proofOfAddress",
    [FileEnum.BANK_STATEMENT]: "bankStatement",
    [FileEnum.FINANCIAL_STATEMENT]: "financialStatement",
    [FileEnum.SALARY_PROOF]: "salaryProof",
    [FileEnum.INVESTMENT_MANDATE]: "investmentMandate",
    [FileEnum.FUND_MANAGER_LICENSE]: "fundManagerLicense",
    [FileEnum.CERTIFICATE_OF_INCORPORATION]: "certificateOfIncorporation",
    [FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS]:
      "supportingDocumentsWithSuppliers",
    [FileEnum.PROOF_OF_BANK_ACCOUNT_DETAILS]: "proofOfBankAccountDetails",
    [FileEnum.CRS_SELF_CERTIFICATION_FORM]: "crsSelCertificationForm",
    [FileEnum.COMPANY_CONSTITUTION]: "companyConstitution",
  };

  const downloadDocument = (path: string, fileName: string) => {
    useGetDocumentMutaiton.mutate(
      { token, path },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, fileName, FileType.PDF);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const uploadDocument = (file: File | null, fileType: FileEnum) => {
    if (!file) {
      toast.error("Uploaded file not found.");

      return;
    }

    useAdminUploadDocumentMutation.mutate(
      {
        token,
        file,
        name: file?.name,
        fileType,
        userId: userData?.id!,
      } as AdminUploadDocumentRequest,
      {
        onSuccess: (data: AdminUploadDocumentReponse) => {
          const newPath = data.path;
          const documentKey = documentMapping[fileType];
          setDocumentPaths((prevPaths) => ({
            ...prevPaths,
            [documentKey]: [...prevPaths[documentKey], newPath],
          }));
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const getNameFromPath = (path: string) => {
    const parts = path.split(`\\`);
    const extractedName = parts.slice(1).join(`\\`);

    return extractedName;
  };

  // Document configurations for rendering
  const documentConfigs = useMemo<DocumentConfig[]>(
    () => [
      {
        field: "certificateOfIncorporation",
        header: "Certificate of Incorporation",
        fileType: FileEnum.CERTIFICATE_OF_INCORPORATION,
      },
      {
        field: "businessProfile",
        header: "Company/Business Profile (within last 3 months)",
        fileType: FileEnum.BUSINESS_PROFILE,
      },
      {
        field: "companyConstitution",
        header: "Company Constitution / Memorandum & Articles of Association",
        fileType: FileEnum.COMPANY_CONSTITUTION,
      },
      {
        field: "shareHoldersDirectorsRegisters",
        header: "Shareholders and Directors Register",
        fileType: FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS,
      },
      {
        field: "balanceSheetPAndLStatement",
        header: "Financial Documents",
        fileType: FileEnum.BALANCE_SHEET,
      },
      {
        field: "proofOfAddress",
        header: "Proof of Residential Address",
        fileType: FileEnum.PROOF_OF_ADDRESS,
      },
      {
        field: "financialStatement",
        header: "Financial Documents",
        fileType: FileEnum.FINANCIAL_STATEMENT,
      },
      {
        field: "salaryProof",
        header: "Financial Documents",
        fileType: FileEnum.SALARY_PROOF,
      },
      {
        field: "proofOfBankAccountDetails",
        header: "Proof of Bank Account Details",
        fileType: FileEnum.PROOF_OF_BANK_ACCOUNT_DETAILS,
      },
      {
        field: "crsSelCertificationForm",
        header: "CRS Self-Certification Form",
        fileType: FileEnum.CRS_SELF_CERTIFICATION_FORM,
      },
      {
        field: "investmentMandate",
        header: "Investment Mandates",
        fileType: FileEnum.INVESTMENT_MANDATE,
      },
      {
        field: "fundManagerLicense",
        header: "Fund Manager License",
        fileType: FileEnum.FUND_MANAGER_LICENSE,
      },
      {
        field: "bankStatement",
        header: "Proof of Bank Account Details",
        fileType: FileEnum.BANK_STATEMENT,
      },
      {
        field: "supportingDocumentsWithSuppliers",
        header: (role?: Persona) =>
          role === Persona.DISTRIBUTOR
            ? "Additional Supporting Documents"
            : "Supporting Documents with Suppliers",
        fileType: FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS,
      },
    ],
    []
  );

  // Helper to get header text (handles role-based logic)
  const getDocumentHeader = (config: DocumentConfig): string => {
    return typeof config.header === "function"
      ? config.header(role)
      : config.header;
  };

  return (
    <>
      <div className={classes.valueContainerDoc}>
        {documentConfigs.map((config) => {
          const documents = documentPaths[config.field];
          if (!documents || documents.length === 0) return null;

          return (
            <DocumentList
              key={config.field}
              documents={documents}
              header={getDocumentHeader(config)}
              fileType={config.fileType}
              uploadDocument={uploadDocument}
              downloadDocument={downloadDocument}
              getNameFromPath={getNameFromPath}
            />
          );
        })}
        {additionalFilesList && additionalFilesList.length > 0 && (
          <div>
            {additionalFilesList.map((doc) => (
              <div className={classes.DocContain}>
                <Text className={classes.additionalDocName}>{doc.docName}</Text>
                <Image
                  onClick={() => downloadDocument(doc.path, doc.docName)}
                  src={downloadbutton}
                  className={classes.downloadIcon}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {spvFilesList.length > 0 && (
        <div style={{ paddingLeft: 44, paddingBottom: 44 }}>
          <Divider my="md" />
          <h2 className={classes.spvHeader}>SPV</h2>
          {spvFilesList.map((doc) => (
            <div className={classes.DocContain}>
              <Text className={classes.additionalDocName}>{doc.docName}</Text>
              <Image
                onClick={() => downloadDocument(doc.path, doc.docName)}
                src={downloadbutton}
                className={classes.downloadIcon}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Documents;
